import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { api } from "../api";
import { filterMfeNames, formatProductName, hasMfeOverlap } from "../product-utils";
import type { Snapshot, Environment, EnvironmentsResponse, MfeVersion, PromotionChange, PromotionRequest, ProductsResponse } from "../types";
import "../components/diff-preview-dialog";
import {
  buildDefaultSnapshotName,
  filterIgnoredMfes,
  getSortedSnapshotVersions,
  getSnapshotTargetEnvironment,
  loadSnapshots,
  persistSnapshots,
} from "../snapshot-utils";
import { buildOverridesFromVersions, encodeDashboardPreviewUrl, getDefaultPreviewRoute } from "../preview-utils";

@customElement("snapshots-page")
export class SnapshotsPage extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: Geist, sans-serif;
    }

    .panel-body {}

    .save-form {
      display: flex;
      align-items: center;
      gap: var(--spacing-8, 8px);
      margin-bottom: var(--spacing-16, 16px);
    }

    .snapshot-list {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-8, 8px);
    }

    bee-card {
      --padding: var(--spacing-16, 16px);
    }

    bee-card::part(body) {
      padding: var(--spacing-12, 12px) var(--spacing-16, 16px);
    }

    bee-card::part(base) {
      transition: box-shadow 0.15s ease;
    }

    bee-card:hover::part(base) {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    .snapshot-item {
      display: flex;
      align-items: center;
      gap: var(--spacing-12, 12px);
      font-size: 13px;
    }

    .snapshot-info {
      flex: 1;
      min-width: 0;
    }

    .snapshot-name {
      font-weight: 500;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .snapshot-meta {
      font-size: 11px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      margin-top: 2px;
    }

    .snapshot-actions {
      display: flex;
      gap: 4px;
    }

    .details-table-wrap {
      max-height: 420px;
      overflow: auto;
    }

    .details-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 13px;
    }

    .details-table th {
      text-align: left;
      padding: var(--spacing-8, 8px);
      border-bottom: 2px solid var(--colors-border-border-gray-300, #e0e3e8);
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
    }

    .details-table td {
      padding: var(--spacing-8, 8px);
      border-bottom: 1px solid var(--colors-border-border-gray-300, #e0e3e8);
    }

    .mono {
      font-family: monospace;
      font-size: 12px;
    }

    .empty {
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      text-align: center;
      font-size: 13px;
      padding: var(--spacing-12, 12px);
    }

    .empty-state-card {
      --padding: var(--spacing-20, 20px);
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-12, 12px);
    }

    .empty-state-title {
      font-size: 18px;
      font-weight: 600;
      color: var(--colors-text-brand-900, #002266);
    }

    .empty-state-copy {
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      font-size: 13px;
      line-height: 1.5;
    }

    .empty-state-list {
      margin: 0;
      padding-left: 18px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      font-size: 13px;
      line-height: 1.6;
    }
  `;

  @property({ attribute: false }) environments: EnvironmentsResponse | null = null;
  @property({ attribute: false }) activeProductFilter: string | null = null;
  @property({ attribute: false }) products: ProductsResponse | null = null;

  @state() private _snapshots: Snapshot[] = [];
  @state() private _name = "";
  @state() private _env: Environment = "stage";
  @state() private _saving = false;
  @state() protected _error = "";
  @state() private _activeSnapshot: Snapshot | null = null;
  @state() private _previewChanges: PromotionChange[] | null = null;
  @state() private _previewRequest: PromotionRequest | null = null;

  connectedCallback() {
    super.connectedCallback();
    this._loadSnapshots();
  }

  private _loadSnapshots() {
    this._snapshots = loadSnapshots();
  }

  private _filteredSnapshots(): Snapshot[] {
    if (!this.activeProductFilter) return this._snapshots;
    return this._snapshots.filter((s) =>
      s.product
        ? s.product === this.activeProductFilter
        : hasMfeOverlap(Object.keys(s.versions), this.activeProductFilter, this.products),
    );
  }

  private _persistSnapshots() {
    persistSnapshots(this._snapshots);
  }

  private async _save() {
    this._saving = true;
    this._error = "";
    try {
      const data = this.environments ?? await api.getEnvironments();
      const envData = data[this._env];
      const allMfeNames = Object.keys(filterIgnoredMfes(envData.mfes));
      const mfeNames = filterMfeNames(allMfeNames, this.activeProductFilter, this.products);
      const versions: Record<string, MfeVersion> = {};
      for (const name of mfeNames) {
        const mfe = envData.mfes[name];
        versions[name] = { major: mfe.major, resolved: mfe.resolved };
      }
      const timestamp = new Date();
      const snapshot: Snapshot = {
        id: crypto.randomUUID(),
        name: this._name.trim() || buildDefaultSnapshotName(timestamp),
        environment: this._env,
        timestamp: timestamp.toISOString(),
        versions,
        product: this.activeProductFilter ?? null,
      };
      this._snapshots = [snapshot, ...this._snapshots];
      this._persistSnapshots();
      this._name = "";
    } catch (e: unknown) {
      this._error = e instanceof Error ? e.message : "Failed to save snapshot";
    } finally {
      this._saving = false;
    }
  }

  private _delete(id: string) {
    this._snapshots = this._snapshots.filter((s) => s.id !== id);
    this._persistSnapshots();
  }

  private async _apply(snapshot: Snapshot) {
    this._error = "";
    const target = getSnapshotTargetEnvironment(snapshot.environment);
    if (!target) return;

    const request: PromotionRequest = {
      source: snapshot.environment,
      target,
      mfes: Object.keys(filterIgnoredMfes(snapshot.versions)).sort(),
      sourceVersions: filterIgnoredMfes(snapshot.versions),
      snapshotName: snapshot.name,
      snapshotTimestamp: snapshot.timestamp,
    };

    try {
      const preview = await api.previewPromotion(request);
      if (preview.changes.length === 0) {
        this._error = "No changes to promote -- all selected MFEs are already up to date.";
        return;
      }
      this._previewChanges = preview.changes;
      this._previewRequest = request;
    } catch (e: unknown) {
      this._error = e instanceof Error ? e.message : "Failed to preview promotion";
    }
  }

  private _closePreview() {
    this._previewChanges = null;
    this._previewRequest = null;
  }

  protected async _openEnvironment(snapshot: Snapshot) {
    this._error = "";

    let environments = this.environments;

    if (!environments) {
      try {
        environments = await api.getEnvironments();
        this.environments = environments;
      } catch (e: unknown) {
        this._error = e instanceof Error ? e.message : "Failed to load environment data";
        return;
      }
    }

    const currentEnvironment = environments[snapshot.environment] ?? null;
    const overrides = buildOverridesFromVersions(
      snapshot.versions,
      currentEnvironment?.mfes ?? {},
    );

    const previewPath = encodeDashboardPreviewUrl({
      environment: snapshot.environment,
      route: getDefaultPreviewRoute(snapshot.product),
      overrides,
    });

    this.dispatchEvent(new CustomEvent("navigate", {
      detail: previewPath,
      bubbles: true,
      composed: true,
    }));
  }

  private _formatDate(dateStr: string): string {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  private _openDetails(snapshot: Snapshot) {
    this._activeSnapshot = snapshot;
  }

  private _closeDetails() {
    this._activeSnapshot = null;
  }

  render() {
    return html`
      ${this._renderContent()}
      ${this._activeSnapshot ? this._renderSnapshotDetails(this._activeSnapshot) : nothing}
      ${this._previewChanges && this._previewRequest
        ? html`<diff-preview-dialog
            .changes=${this._previewChanges}
            .request=${this._previewRequest}
            .environmentData=${this.environments}
            .products=${this.products}
            @close=${() => this._closePreview()}
            @confirmed=${() => {
              this._closePreview();
              this.dispatchEvent(new CustomEvent("navigate", { detail: "/promotions", bubbles: true, composed: true }));
            }}
          ></diff-preview-dialog>`
        : nothing}
    `;
  }

  private _renderContent() {
    const filteredSnapshots = this._filteredSnapshots();

    return html`
      <div class="panel-body">
        ${this._error ? html`<bee-alert variant="error" open><span slot="message">${this._error}</span></bee-alert>` : nothing}
        <div class="save-form">
          <bee-input
            placeholder="Optional snapshot name"
            size="small"
            .value=${this._name}
            @bee-input=${(e: CustomEvent) => { this._name = (e.target as HTMLInputElement).value; }}
          ></bee-input>
          <bee-select
            size="small"
            .value=${this._env}
            @bee-change=${(e: CustomEvent) => { this._env = (e.target as HTMLSelectElement).value as Environment; }}
          >
            <bee-option value="dev">dev</bee-option>
            <bee-option value="stage">stage</bee-option>
          </bee-select>
          <bee-button label=${this._saving ? "Saving..." : "Create Snapshot"} bee-type="primary" size="small" @click=${() => this._save()} ?disabled=${this._saving} ?loading=${this._saving}></bee-button>
        </div>
        <div class="snapshot-list">
          ${this._snapshots.length === 0
            ? this._renderEmptyStateCard(
              "No snapshots yet",
              "Snapshots save the exact MFE versions for an environment so you can compare changes later or reuse that state for a promotion.",
              [
                "Choose dev or stage in the selector above.",
                "Optionally name the snapshot for the release window or investigation you are tracking.",
                "Select Create Snapshot to store the current versions for this product scope.",
              ],
            )
            : filteredSnapshots.length === 0
              ? this._renderEmptyStateCard(
                "No snapshots for this product",
                "Snapshots exist in storage, but none belong to the product currently selected in the dashboard filter.",
                [
                  "Switch the active product filter to see snapshots saved for another product.",
                  "Create a new snapshot while this product is selected to capture only its MFEs.",
                ],
              )
              : filteredSnapshots.map((s) => this._renderSnapshot(s))}
        </div>
      </div>
    `;
  }

  private _renderEmptyStateCard(title: string, copy: string, steps: string[]) {
    return html`
      <bee-card class="empty-state-card" type="neutral">
        <div class="empty-state">
          <div class="empty-state-title">${title}</div>
          <div class="empty-state-copy">${copy}</div>
          <ol class="empty-state-list">
            ${steps.map((step) => html`<li>${step}</li>`)}
          </ol>
        </div>
      </bee-card>
    `;
  }

  private _getEnvironmentTagColor(env: string): string {
    switch (env) {
      case "dev": return "brand";
      case "stage": return "warning";
      default: return "gray";
    }
  }

  private _renderSnapshot(s: Snapshot) {
    const mfeCount = Object.keys(s.versions).length;
    const target = getSnapshotTargetEnvironment(s.environment);
    const applyLabel = target === "prod" ? "Create Release" : target ? `Promote to ${target}` : "Apply unavailable";
    const productLabel = s.product ? formatProductName(s.product) : "All";

    return html`
      <bee-card>
        <div class="snapshot-item">
          <div class="snapshot-info">
          <div class="snapshot-name">${s.name}</div>
          <div class="snapshot-meta">
            <bee-tag color=${this._getEnvironmentTagColor(s.environment)} small>${s.environment}</bee-tag>
            <bee-tag color="gray" small>${productLabel}</bee-tag>
            <bee-tag color="gray" small>${mfeCount} MFEs</bee-tag>
            <bee-tag color="gray" small>${this._formatDate(s.timestamp)}</bee-tag>
          </div>
        </div>
        <div class="snapshot-actions">
          <bee-button label="View versions" bee-type="secondary-grey" size="small" @click=${() => this._openDetails(s)}></bee-button>
          <bee-button label="Open in Preview Builder" bee-type="secondary-grey" size="small" @click=${() => { void this._openEnvironment(s); }}></bee-button>
          <bee-button label=${applyLabel} bee-type=${target === 'prod' ? 'destructive' : 'secondary'} size="small" @click=${() => this._apply(s)} ?disabled=${!target}></bee-button>
          <bee-button label="Delete" bee-type="destructive-tertiary" size="small" @click=${() => this._delete(s.id)}></bee-button>
        </div>
        </div>
      </bee-card>
    `;
  }

  private _renderSnapshotDetails(snapshot: Snapshot) {
    const versions = getSortedSnapshotVersions(snapshot);

    return html`
      <bee-modal
        open
        in-root
        title="Snapshot Versions"
        @bee-request-close=${() => this._closeDetails()}
      >
        <div style="margin-bottom:12px; font-size:13px; color: var(--colors-foreground-fg-gray-700, #5a6072);">
          ${snapshot.name} | ${snapshot.environment} | ${snapshot.product ? formatProductName(snapshot.product) : "All products"} | ${versions.length} MFEs | ${this._formatDate(snapshot.timestamp)}
        </div>
        <div class="details-table-wrap">
          <table class="details-table">
            <thead>
              <tr>
                <th>MFE</th>
                <th>Version</th>
              </tr>
            </thead>
            <tbody>
              ${versions.map(([name, version]: [string, MfeVersion]) => html`
                <tr>
                  <td>${name}</td>
                  <td class="mono">${version.resolved}</td>
                </tr>
              `)}
            </tbody>
          </table>
        </div>
        <div slot="footer" style="display:flex;justify-content:flex-end;width:100%">
          <bee-button label="Close" bee-type="primary" @click=${() => this._closeDetails()}></bee-button>
        </div>
      </bee-modal>
    `;
  }
}
